import axios from "axios";

export const isBrowser = () => typeof window !== "undefined";

const SESSION_TOKEN_HEADER = "X-Session-Token";

export const setSessionToken = (token) => {
  if (isBrowser()) {
    window.localStorage.setItem("sessionToken", token || "");
  }
  applySessionToken();
};

export const getSessionToken = () =>
  isBrowser() ? window.localStorage.getItem("sessionToken") || "" : "";

// Restores the session token onto the shared axios instance so every API call
// (across all services, since they all import the same default axios module)
// sends it. Must be called on app bootstrap since axios defaults don't persist
// across a full page reload.
export const applySessionToken = () => {
  const token = getSessionToken();
  if (token) {
    axios.defaults.headers.common[SESSION_TOKEN_HEADER] = token;
  } else {
    delete axios.defaults.headers.common[SESSION_TOKEN_HEADER];
  }
};

let sessionExpiryInterceptorInstalled = false;

// Session-protected API calls (see RequireSessionAttribute on the API) return 401 when the
// caller has no token or an expired one. Rather than let components fail silently, send the
// user back through Azure AD SSO to mint a fresh session. Installed once at app bootstrap.
export const setupSessionExpiryRedirect = () => {
  if (!isBrowser() || sessionExpiryInterceptorInstalled) return;
  sessionExpiryInterceptorInstalled = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        setSessionToken("");
        const params = new URLSearchParams(window.location.search);
        const id = params.get("id");
        const wo = params.get("wo");

        // Mirrors login.js's own SiteID -> path routing, so re-auth sends the
        // user back into whichever flow (incident/complaint/ESG) they were in
        // instead of always defaulting to incidents.
        let siteID = 1;
        if (window.location.pathname.startsWith("/complaints")) {
          siteID = 2;
        } else if (window.location.pathname.startsWith("/esg")) {
          siteID = 3;
        }

        const signInParams = new URLSearchParams({ siteID });
        if (id) signInParams.set("redirect", id);
        if (wo) signInParams.set("wo", wo);

        window.location.href = `${process.env.GATSBY_API_URL}/SSO/SignIn?${signInParams.toString()}`;
      }
      return Promise.reject(error);
    }
  );
};

export const setCookies = (sn) => {
  if (isBrowser()) {
    window.localStorage.setItem("cookies", JSON.stringify(sn));
  }
};
export const getCookies = () =>
  isBrowser() && window.localStorage.getItem("cookies")
    ? JSON.parse(window.localStorage.getItem("cookies"))
    : "";

export const setSN = (sn) => {
  if (isBrowser()) {
    window.localStorage.setItem("sn", JSON.stringify(sn));
  }
};
export const getSN = () =>
  isBrowser() && window.localStorage.getItem("sn")
    ? JSON.parse(window.localStorage.getItem("sn"))
    : "";

export const setPN = (pn) => {
  if (isBrowser()) {
    window.localStorage.setItem("pn", JSON.stringify(pn));
  }
};
export const getPN = () =>
  isBrowser() && window.localStorage.getItem("pn") !== "undefined"
    ? JSON.parse(window.localStorage.getItem("pn"))
    : "";

export const getUserInfo = () =>
  isBrowser() && window.localStorage.getItem("user") !== "undefined"
    ? JSON.parse(window.localStorage.getItem("user"))
    : "";

export const setUserInfo = (pn) => {
  if (isBrowser()) {
    window.localStorage.setItem("user", JSON.stringify(pn));
  }
};
export const setBusinessUnit = (sn) => {
  if (isBrowser()) {
    window.localStorage.setItem("bu", JSON.stringify(sn));
  }
};
export const getBusinessUnit = () =>
  isBrowser() && window.localStorage.getItem("bu")
    ? JSON.parse(window.localStorage.getItem("bu"))
    : 0;

// Stored separately from the "user" object (same pattern as Business Unit)
// so it survives signOut()'s setUserInfo(null), which wipes the user object
// but was never meant to wipe the user's saved dropdown preferences.
export const setEmployeeType = (et) => {
  if (isBrowser()) {
    window.localStorage.setItem("et", JSON.stringify(et));
  }
};
export const getEmployeeType = () =>
  isBrowser() && window.localStorage.getItem("et")
    ? JSON.parse(window.localStorage.getItem("et"))
    : 0;
